import { useEffect, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, CheckCircle2, XCircle, Clock, History as HistoryIcon, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const STATUS_META = {
  pending: { label: 'Pending', color: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
  joined: { label: 'Joined', color: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  postponed: { label: 'Postponed', color: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
  dropped: { label: 'Dropped', color: 'bg-red-500/15 text-red-600 border-red-500/30' },
};

const EMPTY = { candidate_name: '', company: '', expected_joining_date: '', offered_ctc: '', notes: '', job_id: '', recruiter_id: '' };

export default function JoiningsTab() {
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [action, setAction] = useState(null); // { row, type }
  const [actionForm, setActionForm] = useState({ new_expected_date: '', note: '' });
  const [historyRow, setHistoryRow] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (canManage && recruiterFilter !== 'all') params.recruiter_id = recruiterFilter;
      const { data } = await api.get('/joinings', { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, recruiterFilter]);
  useEffect(() => {
    if (canManage) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
    api.get('/jobs').then(({ data }) => setJobs(data)).catch(() => {});
  }, [canManage]);

  const create = async () => {
    if (!form.candidate_name || !form.company || !form.expected_joining_date) {
      toast.error('Candidate, company & expected date required'); return;
    }
    try {
      const payload = { ...form, job_id: form.job_id || null, recruiter_id: form.recruiter_id || null };
      await api.post('/joinings', payload);
      toast.success('Joining added');
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doAction = async () => {
    if (!action) return;
    const payload = { status: action.type };
    if (action.type === 'postponed') {
      if (!actionForm.new_expected_date) { toast.error('New joining date required'); return; }
      payload.new_expected_date = actionForm.new_expected_date;
    }
    if (actionForm.note) payload.note = actionForm.note;
    try {
      await api.patch(`/joinings/${action.row.id}/action`, payload);
      toast.success(`Marked as ${action.type}`);
      setAction(null); setActionForm({ new_expected_date: '', note: '' }); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete joining for ${row.candidate_name}?`)) return;
    try { await api.delete(`/joinings/${row.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const summary = {
    pending: rows.filter(r => r.status === 'pending').length,
    postponed: rows.filter(r => r.status === 'postponed').length,
    joined: rows.filter(r => r.status === 'joined').length,
    dropped: rows.filter(r => r.status === 'dropped').length,
  };

  return (
    <div>
      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[['pending', 'Pending'], ['postponed', 'Postponed'], ['joined', 'Joined'], ['dropped', 'Dropped']].map(([k, l]) => (
          <button key={k} data-testid={`stat-${k}`} onClick={() => setStatusFilter(k)}
            className={`tactical-card p-4 text-left transition-all ${statusFilter === k ? 'border-primary ring-2 ring-primary/20' : ''}`}>
            <div className={`text-[10px] uppercase tracking-widest ${k === 'joined' ? 'text-emerald-600' : k === 'dropped' ? 'text-red-500' : k === 'postponed' ? 'text-blue-600' : 'text-amber-600'}`}>{l}</div>
            <div className="font-display text-3xl font-black mt-1">{summary[k]}</div>
          </button>
        ))}
      </div>

      {/* Filters + Add */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-10" data-testid="jn-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Recruiter</Label>
            <Select value={recruiterFilter} onValueChange={setRecruiterFilter}>
              <SelectTrigger className="w-56 h-10" data-testid="jn-recruiter-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recruiters</SelectItem>
                {users.filter(u => u.role !== 'admin').map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="ml-auto">
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-joining-btn" className="bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" />Add Offer / Joining</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">New joining</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Candidate name *</Label><Input data-testid="jn-cand" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} /></div>
                <div><Label>Company / Client *</Label><Input data-testid="jn-comp" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                <div><Label>Expected joining date *</Label><Input data-testid="jn-date" type="date" value={form.expected_joining_date} onChange={(e) => setForm({ ...form, expected_joining_date: e.target.value })} /></div>
                <div><Label>Offered CTC</Label><Input value={form.offered_ctc} onChange={(e) => setForm({ ...form, offered_ctc: e.target.value })} placeholder="18 LPA" /></div>
                <div>
                  <Label>Linked job (optional)</Label>
                  <Select value={form.job_id || 'none'} onValueChange={(v) => setForm({ ...form, job_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title} · {j.client}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {canManage && (
                  <div className="col-span-2">
                    <Label>Recruiter (leave empty = yourself)</Label>
                    <Select value={form.recruiter_id || 'me'} onValueChange={(v) => setForm({ ...form, recruiter_id: v === 'me' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="me">Me ({user.name})</SelectItem>{users.filter(u => u.role !== 'admin').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <Button data-testid="jn-save" onClick={create} className="col-span-2 bg-primary hover:bg-primary/90 text-white">Save joining</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <div className="tactical-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Candidate</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Recruiter</TableHead>
              <TableHead>Expected date</TableHead>
              <TableHead>CTC</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="joinings-tbody">
            {rows.map(r => {
              const m = STATUS_META[r.status];
              const expOverdue = r.status === 'pending' && new Date(r.expected_joining_date) < new Date(new Date().setHours(0, 0, 0, 0));
              return (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-medium">{r.candidate_name}</div>
                    {r.notes && <div className="text-xs text-muted-foreground truncate max-w-xs">{r.notes}</div>}
                  </TableCell>
                  <TableCell><Badge variant="secondary">{r.company}</Badge></TableCell>
                  <TableCell className="text-sm">{r.recruiter_name}</TableCell>
                  <TableCell>
                    <div className="text-sm">{new Date(r.expected_joining_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    {expOverdue && <div className="text-[10px] uppercase tracking-widest text-red-500">overdue</div>}
                    {r.original_expected_date && r.original_expected_date !== r.expected_joining_date && (
                      <div className="text-[10px] text-muted-foreground">was: {r.original_expected_date}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.offered_ctc || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className={m.color}>{m.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`jn-actions-${r.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        {r.status !== 'joined' && <DropdownMenuItem onClick={() => setAction({ row: r, type: 'joined' })} data-testid={`mark-joined-${r.id}`}><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" /> Mark as Joined</DropdownMenuItem>}
                        {r.status !== 'postponed' && r.status !== 'dropped' && <DropdownMenuItem onClick={() => setAction({ row: r, type: 'postponed' })} data-testid={`postpone-${r.id}`}><Clock className="w-4 h-4 mr-2 text-blue-500" /> Postpone (new date)</DropdownMenuItem>}
                        {r.status !== 'dropped' && <DropdownMenuItem onClick={() => setAction({ row: r, type: 'dropped' })} data-testid={`drop-${r.id}`}><XCircle className="w-4 h-4 mr-2 text-red-500" /> Mark as Dropped</DropdownMenuItem>}
                        {r.status !== 'pending' && <DropdownMenuItem onClick={() => setAction({ row: r, type: 'pending' })}><Clock className="w-4 h-4 mr-2 text-amber-500" /> Re-open (Pending)</DropdownMenuItem>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setHistoryRow(r)}><HistoryIcon className="w-4 h-4 mr-2" /> View history</DropdownMenuItem>
                        {canManage && <DropdownMenuItem onClick={() => del(r)} className="text-red-500">Delete</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No joinings yet. Click "Add Offer / Joining" to log an offer with the candidate's expected joining date.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Action dialog */}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setActionForm({ new_expected_date: '', note: '' }); } }}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {action?.type === 'joined' ? 'Confirm joining' :
                action?.type === 'postponed' ? 'Postpone joining' :
                action?.type === 'dropped' ? 'Mark as dropped' : 'Re-open joining'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <div className="font-medium">{action?.row?.candidate_name}</div>
              <div className="text-xs text-muted-foreground">→ {action?.row?.company} · Expected {action?.row?.expected_joining_date}</div>
            </div>
            {action?.type === 'postponed' && (
              <div>
                <Label>New expected joining date *</Label>
                <Input data-testid="action-new-date" type="date" value={actionForm.new_expected_date} onChange={(e) => setActionForm({ ...actionForm, new_expected_date: e.target.value })} />
              </div>
            )}
            <div>
              <Label>Note {action?.type === 'dropped' ? '(reason)' : '(optional)'}</Label>
              <Textarea data-testid="action-note" rows={3} value={actionForm.note} onChange={(e) => setActionForm({ ...actionForm, note: e.target.value })}
                placeholder={action?.type === 'dropped' ? 'Why did the candidate drop?' : 'Add a note for the record'} />
            </div>
            <Button data-testid="action-confirm" onClick={doAction} className="w-full bg-primary hover:bg-primary/90 text-white">
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyRow} onOpenChange={(v) => { if (!v) setHistoryRow(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">History — {historyRow?.candidate_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(historyRow?.history || []).map((h, i) => (
              <div key={i} className="tactical-card p-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className={STATUS_META[h.status]?.color || ''}>{h.action}</Badge>
                  <div className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString('en-IN')}</div>
                </div>
                <div className="mt-1 text-xs">Expected: <span className="font-mono">{h.expected_date}</span> · by {h.by_name}</div>
                {h.note && <div className="mt-1 text-sm">{h.note}</div>}
              </div>
            ))}
            {(!historyRow?.history || historyRow.history.length === 0) && <div className="text-sm text-muted-foreground">No history recorded yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
